import React, {useEffect, useMemo, useRef, useState} from "react";
import {
    Box,
    Button, Container, Dialog, DialogActions, DialogTitle,
    FormControl,
    FormHelperText,
    FormLabel,
    IconButton,
    List,
    ListItem, ListItemText, Stack,
    TextField, Typography, useMediaQuery, useTheme
} from "@mui/material";
import {useTranslation} from "react-i18next";
import {REGISTRATIONS} from "../locales/namespaces";
import {v4 as uuidv4} from 'uuid';
import {isUUID} from "../hooks/useMobileRegistrationForm";
import {enqueueSnackbar} from "notistack";
import {handleAxiosErrors} from "../../../helpers/errorHandling";
import {API} from "../../../helpers/constants";
import axios from "axios";
import useUserData from "../../../hooks/user/useUserData";
import CustomAutocomplete from "../../../components/mui/form/autocomplete/autocomplete";
import DeleteIcon from "@mui/icons-material/Delete";
import ClearIcon from "@mui/icons-material/Clear";
import Grid from "@mui/material/Grid2";
import DialogContent from "@mui/material/DialogContent";
import LoadingButton from "@mui/lab/LoadingButton";
import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import ExportExcelFromUrl from "../../../components/mui/app/exportExcelFromUrl/exportExcelFromUrl";
import dayjs from "dayjs";
import {access_user, export_excel_list} from "../../../helpers/rights";
import PermissionHOC from "../../permission/components/permissionHOC/permissionHOC";
import ListItemIcon from "@mui/material/ListItemIcon";


type Props = {};
const RegistrationList = (props: Props) => {
    const {formik, onSubmit, getRestrictions, workers,registrationId} = props
    const {token} = useUserData();
    const {t} = useTranslation(REGISTRATIONS)
    const [selectedWorker, setSelectedWorker] = useState(null);
    const [workTime, setWorkTime] = useState('0');
    const [extraTime, setExtraTime] = useState('0');
    const [editingId, setEditingId] = useState(null);

    const [workerError, setWorkerError] = useState('');
    const [workTimeError, setWorkTimeError] = useState('');
    const [extraTimeError, setExtraTimeError] = useState('');

    const [workerErrorTemp, setWorkerErrorTemp] = useState('');
    const [workTimeErrorTemp, setWorkTimeErrorTemp] = useState('');
    const [extraTimeErrorTemp, setExtraTimeErrorTemp] = useState('');
    //dialog
    const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
    const [deleteId, setDeleteId] = useState('')

    const [editingRowId, setEditingRowId] = useState(null);
    const [loadingRowId, setLoadingRowId] = useState(null); // Για API calls
    const [tempValues, setTempValues] = useState({selectedWorker: "",workTm: "0", extraTm: "0"}); // Προσωρινή αποθήκευση
    const inputRef = useRef(null);

    const [loading, setLoading] = useState(false)
    const [workersData, setWorkersData] = useState(Array.isArray(workers) ? workers : [])

    const [sortByRecent, setSortByRecent] = useState(false);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));


    const sortedProjectWorks = useMemo(() => {
        const list = [...formik.values.projectWorks];

        if (sortByRecent) {
            // Ταξινόμηση κατά ID (όπου υψηλότερο = πιο πρόσφατο)
            return list.sort((a, b) => Number(b.id) - Number(a.id));
        }

        // Αλφαβητική ταξινόμηση με βάση το UserId.Description
        return list.sort((a, b) => {
            const nameA = a.UserId?.Description?.toLowerCase() ?? '';
            const nameB = b.UserId?.Description?.toLowerCase() ?? '';
            return nameA.localeCompare(nameB, 'el');
        });
    }, [formik.values.projectWorks, sortByRecent]);

    const searchWorkers = (evt) => {
        setLoading(true)
        axios({
            method: 'post',
            data: { 'query': evt.target.value, isForeman: 0 },
            url: `${API}/en/fts/project/user/search`,
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        }).then((response) => {
            if (response.data.result.length > 0) {
                setWorkersData(response.data.result)
            }
            setLoading(false)
        }).catch((error) => {
            enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
                variant: 'warning',
                autoHideDuration: 3000,
            })
            setLoading(false)
        })
    }

    const availableWorkers = useMemo(() => {
        const usedUserIds = formik.values.projectWorks //παίρνω το projectWorks από το Formik
            .filter((row) => row.id !== selectedWorker?.id && !!row.UserId?.id) //φιλτράρω και κρατάω όλες τις σειρές των χρηστών εκτός από αυτή που επέλεξα τώρα.
            .map((row) => typeof row.UserId === 'object' ? row.UserId.id : row.UserId); // και από αυτές τις σειρές παίρνω απο την κάθε μία μόνο το id.
        return workersData.filter(worker => !usedUserIds.includes(worker.id)); //Από τη λίστα workersData, φιλτράρουμε εκείνους που δεν έχουν χρησιμοποιηθεί ακόμα στο projectWorks.
    }, [formik.values.projectWorks, selectedWorker?.id, workersData])

    useEffect(() => {
        if (editingRowId && inputRef.current) {
            inputRef.current.focus();
        }
    }, [editingRowId]);

    const validateCurrent = () => {
        let ok = true;
        setWorkerError('');
        setWorkTimeError('');
        setExtraTimeError('');

        if (!selectedWorker) {
            setWorkerError(t('Required'));
            ok = false;
        }
        const w = parseFloat(workTime);
        const x = parseFloat(extraTime) || 0;

        if (workTime === '') {
            setWorkTimeError(t('Required'));
            ok = false;
        } else if (isNaN(w) || (w * 10) % 5 !== 0) {
            setWorkTimeError(t('Only numbers in 0.5 increments are allowed (e.g. 1, 1.5, 2)'));
            ok = false;
        }

        if (extraTime === '') {
            setExtraTimeError(t('Required'));
            ok = false;
        } else if (isNaN(x) || (x * 10) % 5 !== 0) {
            setExtraTimeError(t('Only numbers in 0.5 increments are allowed (e.g. 1, 1.5, 2)'));
            ok = false;
        }

        if (selectedWorker) {
            const restr = formik.values.restrictions.find(r => r.userId === selectedWorker.id) || {};
            const usedWork = restr.workTm || 0;
            const usedTotal = restr.totalTm || 0;
            const currentwork = formik.values.workProj || ''

            if (!isNaN(w) && w + usedWork > 7) {
                setWorkTimeError(t('workTimeMessage', {workTm: usedWork,work:currentwork}));
                ok = false;
            }
            if (!isNaN(w) && !isNaN(x) && w + x + usedTotal > 24) {
                setExtraTimeError(t('extraWorkMessage', {extraWorkTm: usedTotal + x}));
                ok = false;
            }
        }

        return ok;
    };

    const validateCurrentTemp = () => {
        let ok = true;
        setWorkerErrorTemp('');
        setWorkTimeErrorTemp('');
        setExtraTimeErrorTemp('');

        if (!tempValues.selectedWorker) {
            setWorkerErrorTemp(t('Required'));
            ok = false;
        }


        const w = parseFloat(tempValues.workTm);
        const x = parseFloat(tempValues.extraTm) || 0;

        if (tempValues.workTm === '') {
            setWorkTimeErrorTemp(t('Required'));
            ok = false;
        } else if (isNaN(w) || (w * 10) % 5 !== 0) {
            setWorkTimeErrorTemp(t('Only numbers in 0.5 increments are allowed (e.g. 1, 1.5, 2)'));
            ok = false;
        }

        if (tempValues.extraTm === '') {
            setExtraTimeErrorTemp(t('Required'));
            ok = false;
        } else if (isNaN(x) || (x * 10) % 5 !== 0) {
            setExtraTimeErrorTemp(t('Only numbers in 0.5 increments are allowed (e.g. 1, 1.5, 2)'));
            ok = false;
        }

        if (tempValues.selectedWorker) {
            const restr = formik.values.restrictions.find(r => r.userId === tempValues.selectedWorker.id) || {};
            const usedWork = restr.workTm || 0;
            const usedTotal = restr.totalTm || 0;

            const currentRow = editingRowId
                ? formik.values.projectWorks.find(r => r.id === editingRowId)
                : null;

            const adjustedUsedWork = currentRow
                ? usedWork - (currentRow.WorkTm || 0)
                : usedWork;

            const adjustedUsedTotal = currentRow
                ? usedTotal - (currentRow.WorkTm + currentRow.ExtraWorkTm || 0)
                : usedTotal;

            if (!isNaN(w) && w + adjustedUsedWork > 7) {
                setWorkTimeErrorTemp(t('workTimeMessage', { workTm: adjustedUsedWork }));
                ok = false;
            }
            if (!isNaN(w) && !isNaN(x) && w + x + adjustedUsedTotal > 24) {
                setExtraTimeErrorTemp(t('extraWorkMessage', { extraWorkTm: adjustedUsedTotal + x }));
                ok = false;
            }
        }

        return ok;
    };

// gather  existing IDs
    const existingUserIds = formik.values.projectWorks
        .map(r => r.UserId?.id)
        .filter(f => f);
    //.filter(Boolean)

    // const handleAdd = () => {
    //     if (!validateCurrent()) return;
    //
    //     let updatedRows;
    //     let rowObj;
    //         // ADD new
    //         const newId = uuidv4();
    //         const newRow = {
    //             id: newId,
    //             UserId: selectedWorker,
    //             WorkTm: parseFloat(workTime),
    //             ExtraWorkTm: parseFloat(extraTime),
    //             rowStored: false
    //         };
    //         updatedRows = [...formik.values.projectWorks, newRow];
    //         rowObj = newRow;
    //
    //
    //     formik.setFieldValue('projectWorks', updatedRows);
    //     onSubmit(
    //         {
    //             ...formik.values,
    //             projectWorks: updatedRows,
    //             newLine: !rowObj.rowStored
    //         },
    //         rowObj
    //     );
    //
    //     // clear for next
    //     setSelectedWorker(null);
    //     setWorkTime('');
    //     setExtraTime('');
    //     setEditingId(null);
    // };


    const handleAdd = async () => {
        const errors = await formik.validateForm();

        if (Object.keys(errors).length > 0) {
            formik.setTouched({
                ProjectId: true,
                UserId: true,
                WorkDt: true
            });
            return;
        }

        if (!validateCurrent()) return;

        const newId = uuidv4();
        const newRow = {
            id: newId,
            UserId: selectedWorker,
            WorkTm: parseFloat(workTime),
            ExtraWorkTm: parseFloat(extraTime),
            rowStored: false
        };

        const updatedRows = [...formik.values.projectWorks, newRow];
        formik.setFieldValue('projectWorks', updatedRows);
        onSubmit(
            {
                ...formik.values,
                projectWorks: updatedRows,
                newLine: true
            },
            newRow
        );

        setSelectedWorker(null);
        setWorkTime('');
        setExtraTime('');
        setEditingId(null);
    };


    const handleDelete = (id) => {
        if (!isUUID(id)) {
            axios({
                method: 'post',
                url: `${API}/en/fts/project/work/remove`,
                headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
                data: {WorkId: id}
            })
                .then(() => {
                    const filtered = formik.values.projectWorks.filter(r => r.id !== id);
                    formik.setFieldValue('projectWorks', filtered);
                    enqueueSnackbar(t(`Work remove successfully!`), {
                        variant: 'success',
                        autoHideDuration: 3000
                    });
                })
                .catch(error => {
                    enqueueSnackbar(handleAxiosErrors(error, t('An error occurred!')), {
                        variant: 'warning',
                        autoHideDuration: 3000
                    });
                });
            setOpenDeleteDialog(false)
        } else {
            const filtered = formik.values.projectWorks.filter(r => r.id !== id);
            formik.setFieldValue('projectWorks', filtered);
            setOpenDeleteDialog(false)
        }
    };

    const handleOpen = (id) => {
        setDeleteId(id)
        setOpenDeleteDialog(true)
    }

    const handleClear = () => {
        setSelectedWorker(null);
        setWorkTime('');
        setExtraTime('');
        setEditingId(null);
        setWorkerError('');
        setWorkTimeError('');
        setExtraTimeError('');
    };


    const handleUpdate = (itemToUpdate = null) => {
        console.log('itemToUpdate',itemToUpdate)
        // Προσθήκη νέας γραμμής (από τα πάνω πεδία)
        if (!validateCurrentTemp()) return;
        if (itemToUpdate.UserId?.id) getRestrictions([...existingUserIds, itemToUpdate.UserId?.id]);

        if (itemToUpdate) {
            const updatedRows = formik.values.projectWorks.map(r =>
                r.id === itemToUpdate.id ? itemToUpdate : r
            );

            formik.setFieldValue('projectWorks', updatedRows);
            onSubmit(
                {...formik.values, projectWorks: updatedRows, newLine: !itemToUpdate.rowStored},
                itemToUpdate
            );
            setEditingRowId(null);
            return;
        }

        const newRow = {
            id: uuidv4(),
            UserId: selectedWorker,
            WorkTm: parseFloat(workTime),
            ExtraWorkTm: parseFloat(extraTime),
            rowStored: false
        };

        const updatedRows = [...formik.values.projectWorks, newRow];
        formik.setFieldValue('projectWorks', updatedRows);
        onSubmit(
            {...formik.values, projectWorks: updatedRows, newLine: true},
            newRow
        );

        // Εκκαθάριση πεδίων
        setSelectedWorker(null);
        setWorkTime('');
        setExtraTime('');
    };

    const validateAndSave = (item) => {
        if (!tempValues.selectedWorker || !tempValues.workTm || !tempValues.extraTm) {
            enqueueSnackbar(t("Συμπληρώστε όλα τα πεδία"), {
                variant: "error",
            });
            return;
        }
        if (!validateCurrentTemp()) return;

        const updatedItem = {
            ...item,
            UserId: tempValues.selectedWorker,
            WorkTm: parseFloat(tempValues.workTm),
            ExtraWorkTm: parseFloat(tempValues.extraTm)
        };

        handleUpdate(updatedItem);
    };


    return (
        <Box sx={{mt: 2}}>
            <Grid container spacing={2} sx={{marginLeft: '0px'}}>
                <Grid size={{xs: 12, sm: 6, md: 4}}>
                    <FormControl fullWidth error={!!workerError}>
                        <FormLabel required>{t('Worker')}</FormLabel>
                        <CustomAutocomplete
                            identifier="id"
                            value={selectedWorker}
                            options={availableWorkers}
                           // options={workers}
                            labelKey="Description"
                            onInputChange={(event) => searchWorkers(event)}
                            onChange={(e, v) => {
                                setSelectedWorker(v);
                                console.log('v',v)
                                if (v?.id) getRestrictions([...existingUserIds, v.id]);
                            }}
                        />
                        <FormHelperText>{workerError}</FormHelperText>
                    </FormControl>
                </Grid>
                <Grid size={{xs: 12, sm: 6, md: 4}}>
                    <FormControl fullWidth error={!!workTimeError}>
                        <FormLabel required>{t('Work Time')}</FormLabel>
                        <TextField
                            variant="standard"
                            value={workTime}
                            onChange={e => {
                                const sanitizedValue = e.target.value.replace(',', '.');
                                setWorkTime(sanitizedValue);
                                if (selectedWorker?.id) getRestrictions([...existingUserIds, selectedWorker.id]);
                            }}
                            inputProps={{inputMode: 'decimal'}}
                        />
                        <FormHelperText>{workTimeError}</FormHelperText>
                    </FormControl>
                </Grid>
                <Grid size={{xs: 12, sm: 6, md: 4}}>
                    <FormControl fullWidth error={!!extraTimeError}>
                        <FormLabel required>{t('Extra Work Time')}</FormLabel>
                        <TextField
                            variant="standard"
                            value={extraTime}
                            onChange={e => {
                                const sanitizedValue = e.target.value.replace(',', '.');
                                setExtraTime(sanitizedValue);
                                if (selectedWorker?.id) getRestrictions([...existingUserIds, selectedWorker.id]);
                            }}
                            inputProps={{inputMode: 'decimal'}}
                        />
                        <FormHelperText>{extraTimeError}</FormHelperText>
                    </FormControl>
                </Grid>
            </Grid>
            <Box mt={2} size={{xs: 12, sm: 6, md: 4}} gap={1} sx={{display: 'flex', justifyContent: 'space-between'}}>
                <Button size={'small'} variant="contained" onClick={handleAdd}>
                    {/*{editingId ? t('Update') : t('Add')}*/}
                    {t('Add')}
                </Button>
                <Button size={'small'} variant="contained" color={'error'} onClick={handleClear}>
                    {t('Clear')}
                </Button>
            </Box>
            <Box
                size={{xs: 12, sm: 6, md: 4}}
                mt={2}
            >
                <List
                    sx={{
                        mt: 2,
                    }}
                >
                    <Box sx={{width:'100%',display:'flex',justifyContent:'flex-end'}}>
                        <Button
                            variant="outlined"
                            size="small"
                            onClick={() => setSortByRecent(prev => !prev)}
                            sx={{ mb: 2}}
                        >
                            {sortByRecent ? t("Ταξινόμηση Αλφαβητικά") : t("Ταξινόμηση κατά Προσθήκη")}
                        </Button>
                        <PermissionHOC requiredPermissions={[export_excel_list]}>
                        <Box sx={{ textAlign: 'right' }}>
                            <ExportExcelFromUrl
                                url={`${API}/en/fts/reports/export`}
                                data={{
                                    reportType: 3,
                                    isActive: 0,
                                    WorkId:registrationId
                            }}/>
                        </Box>
                        </PermissionHOC>
                    </Box>

                    {sortedProjectWorks.map((item,index) => (
                        <ListItem
                            key={item.id}
                            button
                            selected={item.id === editingId}
                            onContextMenu={(e) => {
                                e.preventDefault();
                                 setTempValues({selectedWorker: item.UserId, workTm: item.WorkTm, extraTm: item.ExtraWorkTm});
                                 setEditingRowId(item.id);
                            }}
                            onTouchStart={(e) => {
                                const timeoutId = setTimeout(() => {
                                    setTempValues({ selectedWorker: item.UserId, workTm: item.WorkTm, extraTm: item.ExtraWorkTm });
                                    setEditingRowId(item.id);
                                }, 600);

                                const clear = () => clearTimeout(timeoutId);
                                e.target.addEventListener("touchend", clear, { once: true });
                                e.target.addEventListener("touchmove", clear, { once: true });
                                e.target.addEventListener("touchcancel", clear, { once: true });
                            }}
                            sx={{
                                py: 1.5,
                                boxShadow: 1,
                                borderRadius: 1,
                                transition: 'box-shadow 0.3s ease, background-color 0.3s ease',
                                '&:hover': {
                                    //backgroundColor: editingRowId !== item.id ? "#f5f5f5" : "inherit",
                                    boxShadow: 4,
                                    bgcolor: 'grey.50',
                                },
                                '&.Mui-selected': {
                                    bgcolor: 'action.selected',
                                    boxShadow: 2,
                                },
                            }}
                        >
                            <ListItemIcon
                                sx={{
                                    minWidth: 56,
                                    mr: 1.5,
                                    justifyContent: "flex-end",
                                    alignSelf: "flex-start",
                                    mt: editingRowId === item.id ? 1.5 : 1,
                                }}
                            >
                                {`${index + 1}.`}
                            </ListItemIcon>


                            <ListItemText
                                sx={{
                                    pl: 0.5,
                                    pr: 2,
                                    "& input": { fontSize: isMobile ? "16px" : "inherit" },
                                }}
                                primary={
                                    editingRowId === item.id ? (
                                            <Grid container spacing={3} alignItems="center">
                                                <Grid xs={6} sm={3}>
                                                    <FormControl fullWidth size="small" sx={{minWidth: '250px'}}>
                                                        <FormLabel required>{t('Worker')}</FormLabel>
                                                        <CustomAutocomplete
                                                            identifier="id"
                                                            value={tempValues.selectedWorker || item.UserId}
                                                            options={availableWorkers}
                                                            // options={workers}
                                                            labelKey="Description"
                                                            onInputChange={(event) => searchWorkers(event)}
                                                            onChange={(e,v) => {
                                                                console.log('UserId',item.UserId)
                                                                if (v?.id) {
                                                                    getRestrictions([...existingUserIds, v.id]);
                                                                }
                                                                console.log('v2',v)
                                                                setTempValues({
                                                                    ...tempValues,
                                                                    selectedWorker: v
                                                                })
                                                            }}
                                                        />
                                                        <FormHelperText>{workerErrorTemp}</FormHelperText>
                                                    </FormControl>
                                                </Grid>
                                                <Grid xs={12} sm={6}>
                                                    {/* Work Time Field */}
                                                    <FormControl fullWidth size="small" sx={{minWidth: '200px'}}>
                                                        <FormLabel required>{t('Work Time')}</FormLabel>
                                                        <TextField
                                                            variant="standard"
                                                            inputRef={inputRef}
                                                            size="small"
                                                            value={tempValues.workTm}
                                                            onChange={(e) => {
                                                                setTempValues({
                                                                    ...tempValues,
                                                                    workTm: e.target.value
                                                                })
                                                                console.log('temp',tempValues)
                                                                if (tempValues.selectedWorker?.id) getRestrictions([...existingUserIds, tempValues.selectedWorker?.id]);
                                                            }
                                                            }
                                                            onKeyDown={(e) => e.key === "Enter" && validateAndSave(item)}
                                                        />
                                                        <FormHelperText>{workTimeErrorTemp}</FormHelperText>
                                                    </FormControl>
                                                </Grid>

                                                <Grid xs={12} sm={6}>
                                                    <FormControl fullWidth size="small" sx={{minWidth: '200px'}}>
                                                        <FormLabel required>{t('Extra Work Time')}</FormLabel>
                                                        {/* Extra Work Time Field */}
                                                        <TextField
                                                            variant="standard"
                                                            size="small"
                                                            value={tempValues.extraTm }
                                                            onChange={(e) => {
                                                                setTempValues({
                                                                    ...tempValues,
                                                                    extraTm: e.target.value
                                                                })
                                                                if (tempValues.selectedWorker?.id) getRestrictions([...existingUserIds, tempValues.selectedWorker.id]);
                                                            }
                                                            }
                                                            onKeyDown={(e) => e.key === "Enter" && validateAndSave(item)}
                                                        />
                                                        <FormHelperText>{extraTimeErrorTemp}</FormHelperText>
                                                    </FormControl>
                                                </Grid>
                                                {/* Save & Cancel Buttons (Optional) */}
                                                <Grid xs={12} sm={6}>
                                                    <IconButton onClick={() => {
                                                        console.log('tempValues',tempValues)
                                                        // Validation check
                                                        if (!tempValues.selectedWorker || !tempValues.workTm || !tempValues.extraTm) {
                                                            enqueueSnackbar(t("Συμπληρώστε όλα τα πεδία"), {variant: "error"});
                                                            return;
                                                        }
                                                        if (tempValues.selectedWorker?.id) getRestrictions([...existingUserIds, tempValues.selectedWorker.id]);
                                                        // Δημιουργία του updated item
                                                        const updatedItem = {
                                                            ...item,
                                                            UserId: tempValues.selectedWorker,
                                                            WorkTm: parseFloat(tempValues.workTm),
                                                            ExtraWorkTm: parseFloat(tempValues.extraTm)
                                                        };

                                                        // Κλήση της handleAddOrUpdate
                                                        handleUpdate(updatedItem);
                                                    }}
                                                                disabled={loadingRowId === item.id}>
                                                        {loadingRowId === item.id ? <CircularProgress size={24}/> :
                                                            <CheckIcon/>}
                                                    </IconButton>
                                                    <IconButton onClick={() => setEditingRowId(null)}>
                                                        <CloseIcon/>
                                                    </IconButton>
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleOpen(item.id)}
                                                        disabled={loadingRowId === item.id}
                                                    >
                                                        {loadingRowId === item.id ? <CircularProgress size={24}/> : <DeleteIcon/>}
                                                    </IconButton>
                                                </Grid>
                                            </Grid>
                                    ) : (
                                            <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                <Typography>{item.UserId.Description}</Typography>
                                                <Typography sx={{ml: 'auto'}}>
                                                    {`${Number(item.WorkTm).toFixed(2)}h / ${Number(item.ExtraWorkTm).toFixed(2)}h`}
                                                </Typography>
                                                <IconButton
                                                    edge="end"
                                                    onClick={() => handleOpen(item.id)}
                                                    disabled={loadingRowId === item.id}
                                                >
                                                    {loadingRowId === item.id ? <CircularProgress size={24}/> : <DeleteIcon/>}
                                                </IconButton>
                                            </Box>
                                    )
                                }
                            />
                            {/*<IconButton edge="end" onClick={() => handleOpen(item.id)}>*/}
                            {/*    <DeleteIcon />*/}
                            {/*</IconButton>*/}
                        </ListItem>
                    ))}
                </List>
            </Box>

            <Box
                mt={2}
                sx={{display: 'flex', justifyContent: 'flex-end', gap: 1}}
            >
                <Box>
                    <strong>{t('Work Time')}:</strong>{' '}
                    {formik.values.projectWorks.reduce((sum, r) => sum + (Number(r.WorkTm) || 0), 0).toFixed(2)}
                </Box>
                <Box>
                    <strong>{t('Extra Work Time')}:</strong>{' '}
                    {formik.values.projectWorks.reduce((sum, r) => sum + (Number(r.ExtraWorkTm) || 0), 0).toFixed(2)}
                </Box>
            </Box>
            <Dialog open={openDeleteDialog}>
                <DialogContent>
                    {t('Delete registration row?')}
                </DialogContent>
                <DialogActions>
                    <Button
                        variant={'contained'}
                        onClick={() => setOpenDeleteDialog(false)}
                        sx={{
                            marginRight: 'auto'
                        }}
                    >
                        {t('Cancel')}
                    </Button>
                    <Button
                        variant="contained"
                        color={'error'}
                        onClick={() => handleDelete(deleteId)}
                    >
                        {t('Confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    )
}
export default RegistrationList